import { MainSectionBox } from './MainSectionBox';
import { Box, Button, CircularProgress, Grow, Stack, styled, Typography, useTheme } from '@mui/material';
import { AccessTime, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { showError } from 'utils/errorHandler';
import { useIsComponentMounted } from 'global/hooks/useIsComponentMounted';
import { IOpportunity } from 'global/interfaces/opportunity';
import { getTopNLatestOpportunities } from 'services/opportunityService';
import { useCustomEventListener } from 'react-custom-events';
import { MessagePublisherEventType } from 'global/enums/messagePublisherEventType';
import { PagedResult } from 'global/interfaces/PagedResult';

const JustifiedBox = styled(Box)(() => ({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StyledTitleTypography = styled(Typography)(() => ({
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default function ItemLatestOpportunities() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [pagedOpps, setPagedOpps] = useState<PagedResult<IOpportunity[]>>({ data: [], total: 0 });
  const isComponentMounted = useIsComponentMounted();

  useEffect(() => {
    loadOpportunities();
  }, []);

  useCustomEventListener(
    MessagePublisherEventType[MessagePublisherEventType.NewOpportunity],
    (res: IOpportunity) => {
      setPagedOpps(current => ({
        data: [res, ...current.data.slice(0, current.data.length - 1)],
        total: 1,
      }));
    },
    [],
  );

  const loadOpportunities = () => {
    if (isComponentMounted.current) {
      setLoading(true);
      setPagedOpps({ data: [], total: 0 });
    }

    getTopNLatestOpportunities(8)
      .then((res: PagedResult<IOpportunity[]>) => {
        if (isComponentMounted.current) {
          setPagedOpps(res);
          setLoading(false);
        }
      })
      .catch(showError);
  };
  return (
    <MainSectionBox>
      <JustifiedBox>
        <Stack direction="row" spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>
            Latest Gigs
          </Typography>
        </Stack>
        {!loading && pagedOpps.data.length > 0 && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="text"
              sx={{ color: theme.palette.primary.dark, mr: '-6px', padding: 1 }}
              endIcon={<ArrowForward />}
              onClick={() => navigate(`/gigs`)}
            >
              View all
            </Button>
          </Stack>
        )}
      </JustifiedBox>
      {loading && (
        <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
          <CircularProgress />
        </Box>
      )}
      {!loading && pagedOpps.data.length === 0 && (
        <Box display={'flex'}>
          <Stack direction="column" spacing={2} mb={2} mt={1}>
            <Typography variant="body2">No new gigs!</Typography>
          </Stack>
        </Box>
      )}
      {!loading && pagedOpps.data.length > 0 && (
        <Grow in={true} timeout={500}>
          <Box mb={2}>
            {pagedOpps.data.map((opportunity, index: number) => (
              <Stack key={index} direction="column" spacing={1.5} mt={3.0}>
                <JustifiedBox>
                  <StyledTitleTypography
                    variant="subtitle2"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => window.open(`/gigs/${opportunity.id}`, '_blank')}
                  >
                    {opportunity.title}
                  </StyledTitleTypography>
                  <Stack direction={'row'} gap={0.5} ml={1}>
                    <AccessTime sx={{ stroke: theme.palette.grey[300] }} />
                    <Typography variant="body2" color={theme.palette.grey[500]} paddingRight={0.5} noWrap>
                      {opportunity.agoDesc}
                    </Typography>
                  </Stack>
                </JustifiedBox>
              </Stack>
            ))}
          </Box>
        </Grow>
      )}
    </MainSectionBox>
  );
}
